/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalLicenseRankListResponse } from '../models/clinical-license-rank-list-response';
import { ClinicalLicenseRankRequest } from '../models/clinical-license-rank-request';
import { ClinicalLicenseRankResponse } from '../models/clinical-license-rank-response';
import { GenericResponse } from '../models/generic-response';

/**
 * Clinical license rank catalog. Separate from privilege workflow mail.
 */
@Injectable({ providedIn: 'root' })
export class ClinicalLicenseRanksService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listClinicalLicenseRanks()` */
  static readonly ListClinicalLicenseRanksPath =
    '/api/v1/clinical-privileges/license-ranks';

  /**
   * List active license ranks.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalLicenseRanks()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalLicenseRanks$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRankListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRanksService.ListClinicalLicenseRanksPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRankListResponse>;
        })
      );
  }

  /**
   * List active license ranks.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalLicenseRanks$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalLicenseRanks(
    params?: {},
    context?: HttpContext
  ): Observable<ClinicalLicenseRankListResponse> {
    return this.listClinicalLicenseRanks$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRankListResponse>
        ): ClinicalLicenseRankListResponse => r.body
      )
    );
  }

  /** Path part for operation `createClinicalLicenseRank()` */
  static readonly CreateClinicalLicenseRankPath =
    '/api/v1/clinical-privileges/license-ranks';

  /**
   * Create a license rank.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClinicalLicenseRank()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalLicenseRank$Response(
    params: {
      body: ClinicalLicenseRankRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRanksService.CreateClinicalLicenseRankPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRankResponse>;
        })
      );
  }

  /**
   * Create a license rank.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClinicalLicenseRank$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalLicenseRank(
    params: {
      body: ClinicalLicenseRankRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRankResponse> {
    return this.createClinicalLicenseRank$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRankResponse>
        ): ClinicalLicenseRankResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteClinicalLicenseRank()` */
  static readonly DeleteClinicalLicenseRankPath =
    '/api/v1/clinical-privileges/license-ranks/{id}';

  /**
   * Deactivate a used rank or remove an unused rank.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClinicalLicenseRank()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalLicenseRank$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRanksService.DeleteClinicalLicenseRankPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Deactivate a used rank or remove an unused rank.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteClinicalLicenseRank$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalLicenseRank(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteClinicalLicenseRank$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateClinicalLicenseRank()` */
  static readonly UpdateClinicalLicenseRankPath =
    '/api/v1/clinical-privileges/license-ranks/{id}';

  /**
   * Update a license rank.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClinicalLicenseRank()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalLicenseRank$Response(
    params: {
      id: number;
      body: ClinicalLicenseRankRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRanksService.UpdateClinicalLicenseRankPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRankResponse>;
        })
      );
  }

  /**
   * Update a license rank.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClinicalLicenseRank$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalLicenseRank(
    params: {
      id: number;
      body: ClinicalLicenseRankRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRankResponse> {
    return this.updateClinicalLicenseRank$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRankResponse>
        ): ClinicalLicenseRankResponse => r.body
      )
    );
  }
}
